/**
 * Signals that an interactive auth redirect has been started and the browser is navigating
 * away — Bug #14687.
 *
 * <b>Why this exists.</b> `useAuth.getAccessToken` used to call `acquireTokenRedirect(...)` —
 * which begins a top-level navigation to Entra `/authorize` — and then `return ''`. The caller
 * could not tell that apart from "here is your token", so it fetched with an empty bearer, got a
 * second 401, and called `handleSessionExpired()` → `logoutRedirect(...)`. The browser then had
 * TWO top-level navigations in flight at once, to `/authorize` and `/logout`.
 *
 * That is exactly what distinguished the broken timeout path from the working manual-logout path
 * (a single `logoutRedirect`), and it is why the fault was reproducible only after a token
 * expired — `InteractionRequiredAuthError` fires nowhere else.
 *
 * Throwing instead of returning `''` makes "I am navigating away" impossible to mistake for a
 * token. Callers MUST let it propagate and must NOT treat it as a session expiry: the redirect
 * already underway IS the recovery.
 */
export class RedirectInProgressError extends Error {
  constructor(message = 'Interactive authentication redirect in progress') {
    super(message);
    this.name = 'RedirectInProgressError';
  }
}

/** True when `error` is the navigating-away sentinel rather than a real failure. */
export function isRedirectInProgress(error: unknown): error is RedirectInProgressError {
  return error instanceof RedirectInProgressError;
}

/**
 * Whether MSAL currently has an interaction underway, from msal-react's `inProgress`.
 *
 * <b>Deliberately fails OPEN on an unknown value.</b> `inProgress` is typed as
 * `InteractionStatus` and msal-react always supplies it, so `undefined` should be impossible — but
 * a naive `inProgress !== InteractionStatus.None` treats `undefined` as "interaction in progress"
 * and would then block sign-in *forever*, not merely after a timeout. That is a strictly worse
 * failure than the double-navigation this guard exists to prevent, so an unknown value is read as
 * "no interaction" and the caller proceeds.
 */
export function isInteractionInProgress(inProgress: string | undefined): boolean {
  return inProgress !== undefined && inProgress !== 'none';
}
